/* eslint-disable prettier/prettier */
import React, { useState, useRef, useEffect } from 'react';
import styles from '../../styles/layout/Sidebar.module.css';
import { useNavigate } from 'react-router-dom';
import LogoutConfirmModal from '../controlVariations/LogoutConfirmModal';
import { useAuth } from '../../hooks/useAuth';
import iconlogout from '../../assets/images/icon/iconlogout.png';
import { useLocation } from 'react-router-dom';

// TYPE DEFINITIONS

const ChevronDownIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="m6 9 6 6 6-6" />
	</svg>
);

const CloseIcon = () => (
	<svg
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			d="M18 6L6 18"
			stroke="#555"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M6 6L18 18"
			stroke="#555"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

export interface NavItem {
	id: string;
	label: string;
	icon?: React.ReactNode;
	subItems?: NavItem[];
	isDisabled?: boolean;
}

export interface ProfileData {
	name: string;
	role: string;
	imageUrl: string;
	completionPercent: number;
}

interface SidebarProps {
	logoSrc: string;
	navItems: any[];
	secondaryNavItems: NavItem[]; // ✅ ADDED: For the bottom navigation section
	profile: ProfileData;
	onNavItemClick: (id: string) => void;
	// New Props for Mobile Responsiveness
	isOpen?: boolean;
	onClose?: () => void;
}

// MAIN COMPONENT

const Sidebar: React.FC<SidebarProps> = ({
	logoSrc,
	navItems,
	secondaryNavItems, // ✅ ADDED
	profile,
	onNavItemClick,
	isOpen,
	onClose,
}) => {
	// const [activeId, setActiveId] = useState<string>('dashboard');
	const [openId, setOpenId] = useState<string | null>(null);
	const [showLogout, setShowLogout] = useState(false);
	const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
	const profileRef = useRef<HTMLDivElement>(null);
	const location = useLocation();

	const activeId = React.useMemo(() => {
		const parts = location.pathname.split('/').filter(Boolean);

		return parts[1] ?? 'dashboard';
	}, [location.pathname]);

	const navigate = useNavigate();
	const { logout } = useAuth();

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				profileRef.current &&
				!profileRef.current.contains(event.target as Node)
			) {
				setShowLogout(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleItemClick = (item: NavItem) => {
		if (item.isDisabled) return;

		onNavItemClick(item.id);
		// setActiveId(item.id);
		if (item.subItems && item.subItems.length > 0) {
			setOpenId(openId === item.id ? null : item.id);
		}
	};

	const renderNavItems = (items: NavItem[]) => {
		return items.map(item => {
			const isCurrentlyDisabled = item.isDisabled === true; // Force boolean check

			return (
				<React.Fragment key={item.id}>
					<li
						className={`
                        ${styles.navItem} 
                        ${activeId === item.id ? styles.active : ''} 
                        ${isCurrentlyDisabled ? styles.disabled : styles.enabled}
                    `}
						onClick={() => {
							if (!isCurrentlyDisabled) {
								handleItemClick(item);
							}
						}}
						style={
							isCurrentlyDisabled
								? { pointerEvents: 'none' }
								: { pointerEvents: 'auto' }
						}
					>
						<span className={styles.navIcon}>{item.icon}</span>
						<span className={styles.navLabel}>{item.label}</span>
						{item.subItems && (
							<span
								className={`${styles.chevronIcon} ${openId === item.id ? styles.open : ''}`}
							>
								<ChevronDownIcon />
							</span>
						)}
					</li>
					{/* Sub-items logic */}
					{item.subItems &&
						openId === item.id &&
						!isCurrentlyDisabled && (
							<ul className={styles.subList}>
								{item.subItems.map(subItem => (
									<li
										key={subItem.id}
										className={`${styles.navItem} ${styles.subItem} ${activeId === subItem.id ? styles.active : ''}`}
										onClick={e => {
											e.stopPropagation();
											handleItemClick(subItem);
										}}
									>
										{subItem.label}
									</li>
								))}
							</ul>
						)}
				</React.Fragment>
			);
		});
	};

	const handleLogout = async () => {
		try {
			await logout();
			navigate('/login', { replace: true });
		} catch (error) {
			console.error('Logout failed');
		} finally {
			// ALWAYS redirect
			navigate('/login', { replace: true });
		}
	};

	const getDisplayRole = (role: string) => {
		if (!role) return '';

		const normalized = role.toLowerCase();

		if (normalized === 'coachee') return 'Learner';
		if (normalized === 'coach') return 'Coach';
		if (normalized === 'superadmin') return 'Admin';

		return role; // fallback
	};

	return (
		<div className={`${styles.sidebar} ${isOpen ? styles.open : ''}`}>
			<div className={styles.sidebarHeader}>
				{/* Mobile Header with Close Button */}
				<div className={styles.mobileHeaderTop}>
					<span className={styles.mobileMenuText}>MENU</span>
					<button className={styles.closeButton} onClick={onClose}>
						<CloseIcon />
					</button>
				</div>

				{/* Desktop Logo */}
				<img className={styles.logo} src={logoSrc} alt="Logo" />
			</div>

			<div className={styles.sidebarContent}>
				{/* ✅ MODIFIED: The two nav sections are now rendered separately */}
				<nav className={styles.sidebarNav}>
					<ul className={styles.navList}>
						{renderNavItems(navItems)}
					</ul>

					{/* ✅ ADDED: Secondary navigation list */}
					<ul
						className={`${styles.navList} ${styles.secondaryNavList}`}
					>
						{renderNavItems(secondaryNavItems)}
					</ul>
				</nav>

				<div className={styles.sidebarProfile}>
					<div className={styles.profileWrapper} ref={profileRef}>
						{showLogout && (
							<button
								className={styles.logoutButton}
								onClick={() => setShowLogoutConfirm(true)}
							>
								<div className={styles.logoutCont}>
									<img
										src={iconlogout}
										alt="error"
										className={styles.logoutimg}
									/>
									<span className={styles.logoutlable}>
										Logout
									</span>
								</div>
							</button>
						)}
						{/* <div
                            className={styles.profileDetails}
                            // onClick={() => setShowLogout(true)}
                            onClick={e => {
                                e.stopPropagation(); // 🔑 VERY IMPORTANT
                                setShowLogout(prev => !prev);
                            }}
                        > */}
						<div
							className={styles.profileDetails}
							onClick={e => {
								e.stopPropagation();
								navigate('/dashboard/profile');
								if (onClose) onClose(); // closes mobile sidebar
							}}
						>
							<img
								className={styles.profileImage}
								src={profile.imageUrl}
								alt="Profile"
							/>
							<div className={styles.profileInfo}>
								<div className={styles.profileName}>
									{profile.name}
								</div>
								<div className={styles.profileRole}>
									{getDisplayRole(profile.role)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			{showLogoutConfirm && (
				<div className={styles.modalOverlay}>
					<div className={styles.modalContainer}>
						<LogoutConfirmModal
							open={showLogoutConfirm}
							onConfirm={handleLogout}
							onCancel={() => setShowLogoutConfirm(false)}
						/>
					</div>
				</div>
			)}
		</div>
	);
};

export default Sidebar;
